import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { KpiCard, MetaStrip, SectionCard, StatusBadge, TableControls } from '../../components/admin/AdminPrimitives';
import {
  formatCurrencyINRFull,
  formatDateIN,
  normalizeWorkflowStatus,
  workflowStatusLabel,
  workflowStatusTone,
} from '../../utils/adminFormat';
import { bulkUpdateReimbursementsStatus, getReimbursements, isExpenseApiError } from '../../services/expenseService';

const SLA_BREACH_HOURS = 30 * 24;

const AdminReimbursementsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [rows, setRows] = useState<Array<{
    id: string;
    trip_id?: string | null;
    payer_name?: string | null;
    payer_department?: string | null;
    reimbursable_amount: number;
    rejected_risk_amount: number;
    status: string;
    payroll_batch_ref?: string | null;
    created_at?: string;
  }>>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'scheduled' | 'paid' | 'failed' | 'cancelled'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const loadData = async () => {
    const reimbursements = await getReimbursements({ perPage: 500 });
    setRows(reimbursements as Array<any>);
    setLastRefreshedAt(new Date());
    setSelectedIds([]);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        await loadData();
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load reimbursements.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const payableTotal = useMemo(() => rows.reduce((sum, row) => sum + Number(row.reimbursable_amount || 0), 0), [rows]);
  const riskTotal = useMemo(() => rows.reduce((sum, row) => sum + Number(row.rejected_risk_amount || 0), 0), [rows]);
  const pendingCount = useMemo(() => rows.filter((row) => normalizeWorkflowStatus(row.status) === 'pending').length, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((row) => {
        const statusRaw = String(row.status || '').toLowerCase();
        if (statusFilter !== 'all' && statusRaw !== statusFilter) return false;
        if (!q) return true;
        const haystack = [
          row.id,
          row.trip_id || '',
          row.payer_name || '',
          row.payer_department || '',
          row.payroll_batch_ref || '',
        ].join(' ').toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => Number(b.reimbursable_amount || 0) - Number(a.reimbursable_amount || 0));
  }, [rows, search, statusFilter]);

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAll = (): void => {
    setSelectedIds((prev) => {
      const pageIds = filteredRows.map((row) => row.id);
      if (pageIds.length > 0 && pageIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !pageIds.includes(id));
      }
      return Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const onBulkStatus = async (status: 'PENDING' | 'SCHEDULED' | 'PAID' | 'FAILED' | 'CANCELLED') => {
    if (selectedIds.length === 0) return;
    try {
      setIsBulkUpdating(true);
      setErrorMessage(null);
      setInfoMessage(null);
      const result = await bulkUpdateReimbursementsStatus({ reimbursement_ids: selectedIds, status });
      setInfoMessage(`${result.updated} reimbursement row(s) updated to ${status}.`);
      await loadData();
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Bulk update failed.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const exportRiskPack = (): void => {
    const riskRows = filteredRows.filter((row) => Number(row.rejected_risk_amount || 0) > 0);
    const escapeCell = (value: string | number | null | undefined): string => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const header = ['reimbursement_id', 'trip_id', 'payer_name', 'department', 'reimbursable_amount', 'risk_amount', 'status', 'sla_age_hours', 'created_at'];
    const rows = riskRows.map((row) => {
      const createdAt = row.created_at ? new Date(row.created_at).getTime() : Date.now();
      const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
      return [
        row.id,
        row.trip_id || '',
        row.payer_name || '',
        row.payer_department || '',
        Number(row.reimbursable_amount || 0),
        Number(row.rejected_risk_amount || 0),
        row.status || '',
        ageHours,
        row.created_at || '',
      ];
    });
    const csv = [header.join(','), ...rows.map((row) => row.map((cell) => escapeCell(cell as string | number)).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `risk-reimbursements-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  return (
    <AdminShell pageTitle="Reimbursements" subtitle="Payroll-linked reimbursement queue with a maximum 30-day payout SLA.">
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}
      {infoMessage ? <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{infoMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left="Reporting period: current reimbursement cycle (max payout SLA: 30 days)"
            right={`Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <div className="grid gap-3 md:grid-cols-3">
            <KpiCard label="Approved Unpaid Amount" value={formatCurrencyINRFull(payableTotal)} helper={`${rows.length} reimbursement entries`} />
            <KpiCard label="At-Risk Reimbursement Amount" value={formatCurrencyINRFull(riskTotal)} helper="Current reimbursement cycle" tone={riskTotal > 0 ? 'danger' : 'positive'} />
            <KpiCard label="Pending Payout Count" value={String(pendingCount)} helper="Payroll/finance queue" tone={pendingCount > 0 ? 'warning' : 'positive'} />
          </div>

          <SectionCard
            title="Payout Action Queue"
            subtitle="Pending reimbursement entries sorted by reimbursable amount and tracked against the 30-day SLA."
            actions={
              rows.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={exportRiskPack} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Export Risk Pack</button>
                  {selectedIds.length > 0 ? <span className="text-xs font-semibold text-slate-600">{selectedIds.length} selected</span> : null}
                  {selectedIds.length > 0 ? <button type="button" disabled={isBulkUpdating} onClick={() => onBulkStatus('SCHEDULED')} className="rounded-full bg-slate-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Bulk Schedule</button> : null}
                  {selectedIds.length > 0 ? <button type="button" disabled={isBulkUpdating} onClick={() => onBulkStatus('PAID')} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Bulk Mark Paid</button> : null}
                  {selectedIds.length > 0 ? <button type="button" disabled={isBulkUpdating} onClick={() => onBulkStatus('CANCELLED')} className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Bulk Cancel</button> : null}
                </div>
              ) : null
            }
          >
            {rows.length > 0 ? (
              <>
                <TableControls
                  searchValue={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search reimbursement ID, trip, payer, department, batch..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value as 'all' | 'pending' | 'scheduled' | 'paid' | 'failed' | 'cancelled')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </label>
                  )}
                />

                {filteredRows.length === 0 ? (
                  <p className="text-sm font-semibold text-slate-600">No reimbursements match the selected filters.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1180px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">
                            <input
                              type="checkbox"
                              checked={filteredRows.length > 0 && filteredRows.every((row) => selectedIds.includes(row.id))}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th className="pb-2">Entry</th>
                          <th className="pb-2">Trip</th>
                          <th className="pb-2">Payer</th>
                          <th className="pb-2">Department</th>
                          <th className="pb-2 text-right">Reimbursable</th>
                          <th className="pb-2 text-right">Risk</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">SLA Age</th>
                          <th className="pb-2">Payroll Batch</th>
                          <th className="pb-2">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((row) => {
                          const status = normalizeWorkflowStatus(row.status);
                          const createdAt = row.created_at ? new Date(row.created_at).getTime() : Date.now();
                          const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
                          const isBreach = status === 'pending' && ageHours >= SLA_BREACH_HOURS;
                          return (
                            <tr key={row.id} className="border-b border-slate-100 text-sm text-slate-700">
                              <td className="py-3">
                                <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                              </td>
                              <td className="py-3 font-semibold text-slate-900">{row.id.slice(0, 8)}</td>
                              <td className="py-3">{row.trip_id ? row.trip_id.slice(0, 8) : '-'}</td>
                              <td className="py-3">{row.payer_name || '-'}</td>
                              <td className="py-3">{row.payer_department || '-'}</td>
                              <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(Number(row.reimbursable_amount || 0))}</td>
                              <td className="py-3 text-right font-semibold text-red-700">{formatCurrencyINRFull(Number(row.rejected_risk_amount || 0))}</td>
                              <td className="py-3"><StatusBadge label={workflowStatusLabel(status)} tone={workflowStatusTone(status)} /></td>
                              <td className="py-3">
                                <div className="inline-flex items-center gap-2">
                                  <span>{ageHours}h</span>
                                  {isBreach ? <StatusBadge label="Breach" tone="danger" /> : null}
                                </div>
                              </td>
                              <td className="py-3">{row.payroll_batch_ref || 'Unbatched'}</td>
                              <td className="py-3">{formatDateIN(row.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No pending payouts. Queue is clear.</p>
            )}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminReimbursementsPage;
